import { StackNavigationOptions, TransitionPresets } from '@react-navigation/stack';
import {
  ALL_MACHINES,
  isPhone,
  isWeb,
  JOB_STATUSES,
  JOB_STATUSES_TEXT,
  machineStatus,
  SCREEN_PADDING_HORIZONTAL as screenPaddingHorizontal,
  searchDisplayMode,
} from '../../../../../helpers';
import { bottomBarHeight, scale } from '@components/frame';
import i18n from 'i18n-js';
import { ColorFunction } from '@styles/colors/types';
import { Perspective } from '@v2/pages/common/enums';
import { filterTimelineFromEnum } from '@v2/pages/common/helpers';
import { PartLevelSubScreen, PartLevelSubScreenLink } from '@v2/pages/part/details/common/enums/partDetails.enum';
import { JobLevelSubScreen, JobLevelSubScreenLink } from '@v2/pages/job/common/enums/jobDetails.enum';
import { BatchLevelSubScreen, BatchLevelSubScreenLink } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { ProductionOrderLevelSubScreen, ProductionOrderLevelSubScreenLink } from '@v2/pages/productionOrder/common/enums/productionOrderDetails.enum';
import type { RankBadgeVariant } from '@v2/pages/dashboard/common/types/rows';
import type { DashboardScope, DashboardResultType } from '@api/domain/types';
import type { DashboardDrilldownScope, DashboardDrilldownTab } from '@v2/pages/dashboard/common/types/drilldown';
import { NCFileLevelSubScreen, NCFileLevelSubScreenLink } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { StatusBadgeConfig } from '@components/common/StatusBadge/types';

export const LIST_WIDTH = 360;
export const INFO_SCREEN_LIST_WIDTH = 360;

export const INFO_SCREEN_MARGIN_TOP = 41;
export const MACHINES_SCREENS_TOP_BLOCK_HEIGHT_NOT_PHONE = 29;

export const PANEL_VIEW_MODE_WIDTH = 1278;
export const NOT_PANEL_VIEW_MODE_WIDTH = 918;
export const IMAGES_PANEL_VIEW_MODE_WIDTH = 361;

// Centered width of a performance metric chart block (Active/Productive/Paid/Utilization)
export const PERFORMANCE_CHART_BLOCK_WIDTH = 532;

export const SCREEN_PADDING_HORIZONTAL = screenPaddingHorizontal;
export const SCREEN_PADDING_HORIZONTAL_FOR_ITEM = 2;
export const SCREEN_PADDING_HORIZONTAL_FOR_PAGE = SCREEN_PADDING_HORIZONTAL - SCREEN_PADDING_HORIZONTAL_FOR_ITEM;
export const SCREEN_PADDING_VERTICAL = isPhone ? 12 : 18;
export const SCREEN_PADDING_VERTICAL_FOR_ITEM = 2;
export const SCREEN_PADDING_VERTICAL_FOR_HEADER = SCREEN_PADDING_VERTICAL - SCREEN_PADDING_VERTICAL_FOR_ITEM;
export const SCREEN_MARGIN_BOTTOM_PHONE = 11;
export const SCREEN_MARGIN_BOTTOM_WEB = 20;
export const SCREEN_BORDER_RADIUS = 18;
export const INPUT_LINE_HEIGHT = 42;

export const BACK_BUTTON_OFFSET_LEFT = 9;

export const PHONE_LIST_HEADER_MARGIN_TOP = 45;
export const PHONE_DETAILS_HEADER_MARGIN_TOP = 17;

export const BACK_BUTTON_SETTINGS_TEXT = 'Settings';

export const PHONE_DETAILS_HEADER_GAP = 12;

export const LINE_HORIZONTAL_PADDING = 15;
export const LINE_HORIZONTAL_PADDING_WITH_ACTION_ARROW = 10;

export const BATCH_LIST_GAP = 18;

export const MACHINES_PAGES_CONTAINER_STYLES = {
  paddingTop: scale(SCREEN_PADDING_VERTICAL_FOR_ITEM),
  paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
  flex: 1,
};

export const DETAILS_SCREENS_CONTAINER_STYLES_FOR_SCROLL_VIEW = {
  paddingTop: scale(SCREEN_PADDING_VERTICAL_FOR_ITEM),
  paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL_FOR_ITEM),
};

export const DETAILS_SCREENS_CONTAINER_STYLES = {
  ...DETAILS_SCREENS_CONTAINER_STYLES_FOR_SCROLL_VIEW,
  flex: 1,
};

export const DETAILS_DOCUMENTS_SCREENS_CONTAINER_STYLES = { flex: 1, marginHorizontal: -scale(SCREEN_PADDING_HORIZONTAL_FOR_PAGE) };

export const NAVIGATION_CONFIG = {
  headerShown: false,
  animationEnabled: false,
  detachPreviousScreen: isWeb,
} satisfies StackNavigationOptions;

export const BOTTOM_SHEET_NAVIGATION_CONFIG = {
  headerShown: false,
  animationEnabled: !isWeb,
  gestureEnabled: false,
  cardStyle: { backgroundColor: 'transparent', display: 'flex' },
  detachPreviousScreen: false,
  ...TransitionPresets.ModalSlideFromBottomIOS,
} satisfies StackNavigationOptions;

export const BOTTOM_SHEET_SCREEN_CONFIG = {
  ...BOTTOM_SHEET_NAVIGATION_CONFIG,
  cardStyle: {
    backgroundColor: 'transparent',
  },
};

export const BASIC_SCREEN_OPTIONS = {
  options: {
    presentation: isPhone ? 'modal' : 'transparentModal',
  },
} as const;

export const BOTTOM_SHEET_OPTIONS: StackNavigationOptions = {
  presentation: isPhone ? 'modal' : 'transparentModal',
  gestureEnabled: false,
  animationEnabled: !isWeb,
  cardStyle: { backgroundColor: 'transparent', display: 'flex' },
  detachPreviousScreen: isWeb,
  ...TransitionPresets.ModalSlideFromBottomIOS,
};

export const BOTTOM_SHEET_WITH_GESTURE_OPTIONS: StackNavigationOptions = {
  ...BOTTOM_SHEET_OPTIONS,
  gestureEnabled: true,
  animationEnabled: true,
};

export enum CHART_TYPE {
  LINE = 'line',
  BAR = 'bar',
}

export const ALL_MACHINES_OBJECT = {
  id: ALL_MACHINES,
  get fullName() {
    return i18n.t('All Machines');
  },
  get shortName() {
    return i18n.t('All Machines');
  },
  allSelected: true,
  machineStatus: {
    status: machineStatus.ACTIVE,
  },
};

export const SMART_FACTORY = 'Smart Factory';

export const APP_STORE_URL = 'https://apps.apple.com/ua/app/smart-factory/id1510565698';
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.smart.factory';

export const DETAILS_SCREEN_IMAGES_DOCUMENTS_DISPLAY_MODE_KEY = 'DetailsScreenImagesDocuments-displayMode';

export const BATCH_LIST_SELECT = [
  'ActualActiveTimeAverageInSeconds',
  'ActualActiveTimeTotalInSeconds',
  'ActualChangeTimeTotalInSeconds',
  'ActualCycleTimeTotalInSeconds',
  'IdealActiveTimeTotalInSeconds',
  'IdealChangeTimeTotalInSeconds',
  'IdealCycleTimeTotalInSeconds',
  'ActualChangeTimeAverageInSeconds',
  'ActualCycleTimeAverageInSeconds',
  'ActualTotalTimeTotalInSeconds',
  'ActualSetupTimeInSeconds',
  'BatchNumber',
  'BatchSequenceNumber',
  'CreatedBy',
  'CreatedOn',
  'DurationInSeconds',
  'EndedOn',
  'IdealActiveTimeSetManually',
  'IdealChangeTimeSetManually',
  'IdealCycleTimeSetManually',
  'IdealSetupTimeInSeconds',
  'IdealSetupTimeSetManually',
  'IdealTotalTimeTotalInSeconds',
  'IsClosed',
  'IsInProgress',
  'Links',
  'LinkStatus',
  'LinksSummary',
  'MachineFullName',
  'MachineId',
  'MachineShortName',
  'ModifiedBy',
  'ModifiedOn',
  'NcFileId',
  'NcFileName',
  'NcFileStatus',
  'ProductionBatchId',
  'Quantity',
  'ScheduledTotalTimeTotalInSeconds',
  'ScheduledActiveTimeTotalInSeconds',
  'ScheduledSetupTimeInSeconds',
  'ScheduledChangeTimeTotalInSeconds',
  'StartedOn',
  'ImageDocumentId',
  'ImageHash',
  'ImageData',
];

export const REPLACEMENT_PART_FOR_DETAILS_NAVIGATION_LOGIC = 'V2';
export const DATE_FORMAT = 'YYYY-MM-DD';

export const NARROW_WEB_BREAKPOINT = 1080;
export const LABEL_PATTERN = 'lbl';

export const HEADER_MOBILE_GAP_BETWEEN_LEVELS = 6;

export const DETAILS_SCREEN_TAB_NAME = {
  Part: 'Part',
  Order: 'Production Order',
  Jobs: 'Jobs',
  Batch: 'Batch',
  NCFile: 'NC File',
  Tools: 'Tools',
};

export const singularTabNameMap = {
  [DETAILS_SCREEN_TAB_NAME.Batch]: 'Batch',
  [DETAILS_SCREEN_TAB_NAME.NCFile]: 'NC File',
  [DETAILS_SCREEN_TAB_NAME.Order]: 'Production Order',
  [DETAILS_SCREEN_TAB_NAME.Part]: 'Part',
  [DETAILS_SCREEN_TAB_NAME.Jobs]: 'Job',
  [DETAILS_SCREEN_TAB_NAME.Tools]: 'Tool',
};

export const pluralTabNameMap = {
  [DETAILS_SCREEN_TAB_NAME.Batch]: 'Batches',
  [DETAILS_SCREEN_TAB_NAME.NCFile]: 'NC Files',
  [DETAILS_SCREEN_TAB_NAME.Order]: 'Production Orders',
  [DETAILS_SCREEN_TAB_NAME.Part]: 'Parts',
  [DETAILS_SCREEN_TAB_NAME.Jobs]: 'Jobs',
  [DETAILS_SCREEN_TAB_NAME.Tools]: 'Tools',
};

export const pluralTitleRegExp = /^#\d+$/;

export const modalBg = isPhone ? 'Background/Light/Panel' : 'Background/Light/Modal';
export const modalFieldBg = isPhone ? 'Background/Light/Field on Panel' : 'Background/Light/Field on modal';

export const ICON_BUTTON_PADDING = 7;

export const DATA_GRID_IMAGE_COLUMN_WIDTH = 78;

export const IMAGE_HANDLING_MOBILE_FOOTER_HEIGHT = bottomBarHeight;

export const SETTINGS_HEADER_MARGIN_BOTTOM = 18;
export const SETTINGS_ITEM_SHADOW_OFFSET = 2;
export const SETTINGS_HEADER_MARGIN_BOTTOM_WITH_SHADOW_OFFSET = SETTINGS_HEADER_MARGIN_BOTTOM - SETTINGS_ITEM_SHADOW_OFFSET;

export const PADDING_HORIZONTAL_FOR_SHADOW = 2;

export const INFO_SCREEN_DOCUMENTS_DISABLED_MODES = [searchDisplayMode.tableCompact, searchDisplayMode.square];

export const BOTTOM_SHEET_TOP_OFFSET_WEB = 12;
export const BOTTOM_SHEET_TOP_OFFSET = 44;

export const INTERVAL_FOR_UPDATING_MACHINES_ON_INFO_SCREENS = 5000;

export const getJobOrOrderStatusConfig = (status: (typeof JOB_STATUSES)[keyof typeof JOB_STATUSES], C: ColorFunction): StatusBadgeConfig => ({
  running: {
    condition: status === JOB_STATUSES.RUNNING,
    state: {
      styles: { backgroundColor: C('ProdJobBatch/Light/Running'), color: C('Label/Light/AlwaysLight Primary') },
      text: JOB_STATUSES_TEXT[JOB_STATUSES.RUNNING],
      icon: 'runningMan',
      width: 9,
      height: 10.5,
      transform: 'translate(0, -0.5)',
    },
  },
  paused: {
    condition: status === JOB_STATUSES.PAUSED,
    state: {
      styles: { backgroundColor: C('ProdJobBatch/Light/Paused'), color: C('Label/Light/AlwaysLight Primary') },
      text: JOB_STATUSES_TEXT[JOB_STATUSES.PAUSED],
      icon: 'pause',
      width: 6,
      height: 12,
      transform: 'translate(0, -0.3)',
    },
  },
  finished: {
    condition: status === JOB_STATUSES.FINISHED,
    state: {
      styles: { backgroundColor: C('ProdJobBatch/Light/Finished'), color: C('Label/Light/AlwaysLight Primary') },
      text: JOB_STATUSES_TEXT[JOB_STATUSES.FINISHED],
      icon: 'finishFlag',
      width: 9.5,
      height: 10,
      transform: 'translate(0, -0.5)',
    },
  },
  skipped: {
    condition: status === JOB_STATUSES.SKIPPED,
    state: {
      styles: { backgroundColor: C('Status/Light/Not Productive'), color: C('Label/Light/AlwaysLight Primary') },
      text: JOB_STATUSES_TEXT[JOB_STATUSES.SKIPPED],
      icon: 'skipped',
      width: 12,
      height: 7,
    },
  },
  notStarted: {
    condition: status === JOB_STATUSES.NOT_STARTED,
    state: {
      styles: {
        backgroundColor: 'transparent',
        color: C('Status/Light/Not Productive'),
        borderColor: C('Status/Light/Not Productive'),
        borderWidth: scale(1),
        borderStyle: 'dashed',
      },
      text: JOB_STATUSES_TEXT[JOB_STATUSES.NOT_STARTED],
      icon: 'dotsClock',
      width: 15,
      height: 19,
    },
  },
  start: {
    condition: status === JOB_STATUSES.START,
    state: {
      styles: { backgroundColor: C('Status/Light/Active'), color: C('Label/Light/AlwaysLight Primary') },
      text: JOB_STATUSES_TEXT[JOB_STATUSES.START],
      icon: 'play',
      width: 7,
      height: 9,
    },
  },
});

export const ALL_JOBS_ID = 'all';
export const OP_INFO_TABS_WIDTH = 270;

export const IMAGE_UPLOAD_FOOTER_TEXT = {
  AddImageOrDocument: 'Add existing Image or Document in Smart Factory',
  AddNcFile: 'Add existing NC File in Smart Factory',
  BrowseImageOrDocument: 'Browse existing Images & Documents in Smart Factory',
};

export const IMAGE_UPLOAD_MAIN_TEXT = {
  ImageOrDocument: 'Drag Image or Document Here',
  NcFile: 'Drag NC File Here',
  User: 'Drag Image or Document of User Here',
  Machine: 'Drag Image or Document of Machine Here',
};

export const IMAGE_UPLOAD_HEADER_TEXT = {
  AddImageOrDocument: 'Add Image Or Document',
  AddNcFile: 'Add NC File',
  AddNcFileVersion: 'Add NC File Version',
  AddToNcFile: 'Add to NC File',
  AddUserImage: 'Add User Image',
  AddToMachine: 'Add to Machine',
};

export const IMAGE_UPLOAD_BUTTON_TEXT = {
  short: 'Add Existing...',
  long: 'Add Existing Image or Document...',
};

export const LANGUAGE_LABELS_FEATURE_FLAG = 'settings-show-languages-label-switch';

export const ITEM_RELATIONS_FEATURE_FLAG = 'general-show-item-relations';

// Tile constants
export const TILE_CONFIG = {
  BASE_WIDTH: 324,
  BASE_HEIGHT: 216,
  BASE_WIDTH_SMALL: 158,
  BORDER_RADIUS: 18,

  MARGIN_BOTTOM_NORMAL: 22,
  MARGIN_BOTTOM_SMALL: 8,
  SPACE_BETWEEN_NORMAL: 18,
  SPACE_BETWEEN_SMALL: 8,

  LINE_MODE_WIDTH: 66,
  LINE_MODE_HEIGHT: 44,
};

export const TILE_ASPECT_RATIO = TILE_CONFIG.BASE_HEIGHT / TILE_CONFIG.BASE_WIDTH;

// Planboard tile constants
export const PLANBOARD_TILE_CONFIG = {
  CARD_WIDTH: 246,
  CARD_HEIGHT: 230,
  CARD_HEIGHT_UNASSIGNED: 250, // Extra height for operation name row
  // Modal positioning constants
  MODAL_WIDTH: 320,
  MODAL_HEIGHT_ESTIMATE: 650,
  MODAL_EDGE_PADDING: 12,
  // Card menu constants
  MENU_WIDTH: 246,
  MENU_PADDING: 4,
  // Column constants
  COLUMN_CONTENT_WIDTH: 266,
  COLUMN_PADDING_HORIZONTAL: 18,
};

// Perspective navigation params structure
export const PERSPECTIVE_PARAMS_MAP = {
  [Perspective.BATCH]: ['perspective', 'batchNumber', 'batchId', 'ncFileStatus'] as const,
  [Perspective.NC_FILE]: ['perspective', 'ncFileId', 'ncFileName', 'ncFileStatus'] as const,
  [Perspective.PART]: ['perspective', 'partId', 'partName'] as const,
  [Perspective.JOBS]: ['perspective', 'productionOperationId', 'jobName', 'productionOrderId', 'keepList', 'selectedProductionOperationId'] as const,
  [Perspective.ORDER]: ['perspective', 'productionOrderId', 'productionOrderName'] as const,
  [Perspective.TOOLS]: ['perspective', 'toolId', 'toolName'] as const,
} as const;

export const WebDetailsScreenName = 'WebDetails';
export const FullNCFileViewDetailsScreenName = 'FullNCFileViewDetails';

export const BOTTOM_SHEET_ENABLED = isWeb;

export const MODAL_ANIMATION_TIMING = 300;
export const BOTTOM_SHEET_ANIMATION_TIMING = 300;
export const CLOSE_ALL_BOTTOM_SHEETS = '__CLOSE_ALL__';

export const filteredPartLevelSubScreen = filterTimelineFromEnum(PartLevelSubScreen, PartLevelSubScreen.TIMELINE);
export const filteredPartLevelSubScreenLink = filterTimelineFromEnum(PartLevelSubScreenLink, PartLevelSubScreenLink.TIMELINE);
export const filteredJobLevelSubScreen = filterTimelineFromEnum(JobLevelSubScreen, JobLevelSubScreen.TIMELINE);
export const filteredJobLevelSubScreenLink = filterTimelineFromEnum(JobLevelSubScreenLink, JobLevelSubScreenLink.TIMELINE);
export const filteredBatchLevelSubScreen = filterTimelineFromEnum(BatchLevelSubScreen, BatchLevelSubScreen.TIMELINE);
export const filteredBatchLevelSubScreenLink = filterTimelineFromEnum(BatchLevelSubScreenLink, BatchLevelSubScreenLink.TIMELINE);

// Based on task SF-4963
// export const filteredNCFileLevelSubScreen = filterTimelineFromEnum(NCFileLevelSubScreen, NCFileLevelSubScreen.TIMELINE);
// export const filteredNCFileLevelSubScreenLink = filterTimelineFromEnum(NCFileLevelSubScreenLink, NCFileLevelSubScreenLink.TIMELINE);
export const filteredNCFileLevelSubScreen = NCFileLevelSubScreen;
export const filteredNCFileLevelSubScreenLink = NCFileLevelSubScreenLink;

export const filteredProductionOrderLevelSubScreen = filterTimelineFromEnum(ProductionOrderLevelSubScreen, ProductionOrderLevelSubScreen.TIMELINE);
export const filteredProductionOrderLevelSubScreenLink = filterTimelineFromEnum(
  ProductionOrderLevelSubScreenLink,
  ProductionOrderLevelSubScreenLink.TIMELINE
);

export const TABLE_NAMES = {
  part: 'part',
  productionOperation: 'productionOperation',
  productionBatch: 'productionBatch',
  productionOrder: 'productionOrder',
  ncFile: 'ncFile',
  tools: 'tools',
  images: 'images',
  batchList: 'batchList',
  detailsImagesDocuments: 'detailsImagesDocuments',
  productionOrderJobs: 'productionOrderJobs',
  operatorPanelSearchHistory: 'operatorPanelSearchHistory',
  operatorPanelSearchNCFiles: 'operatorPanelSearchNCFiles',
  operatorPanelScheduled: 'operatorPanelScheduled',
  machineScheduled: 'machineScheduled',
  imageUpload: 'imageUpload',
  attachment: 'attachment',
} as const;

export const TEST_IDS = {
  factoryTimeAlert: {
    okButton: 'factory-time-alert-ok',
  },
  liveScreen: {
    topBlockWrapper: 'live-top-block-wrapper',
    topBlockLinkedUnknown: 'live-top-block-linked-unknown',
    topBlockLinkedUnknownHeader: 'live-top-block-linked-unknown-header',
    topBlockChangeIgnored: 'live-top-block-change-ignored',
    topBlockModifyButton: 'live-top-block-modify-button',
    topBlockSetup: 'live-top-block-setup',
    batchBlock: 'live-batch-block',
    activityBlock: 'live-activity-block',
    oeeBlock: 'live-oee-block',
    toolsBlock: 'live-tools-block',
    ncFileBlock: 'live-nc-file-block',
    ncFileBlockVersions: 'live-nc-file-block-versions',
    ncFileBlockContent: 'live-nc-file-block-content',
    allMachinesButton: 'live-all-machines-button',
    allMachinesBlock: 'live-all-machines-gauge',
    singleMachineOverview: 'live-single-machine-overview',
  },
  lineChart: {
    loadingContainer: 'line-chart-loading-container',
    chartContainer: 'line-chart-container',
    chart: 'line-chart',
    legendContainer: 'line-chart-legend-container',
  },
  barChart: {
    loadingContainer: 'bar-chart-loading-container',
    chartContainer: 'bar-chart-container',
    barsContainer: 'bar-chart-bars-container',
  },
  chart: {
    outerContainer: 'chart-outer-container',
    innerContainer: 'chart-inner-container',
    webChartArea: 'chart-web-chart-area',
    mobileFlatListContainer: 'chart-mobile-flatlist-container',
  },
  chartHeader: {
    container: 'chart-header-container',
    toggleButton: 'chart-header-toggle-button',
    toggleToBarButton: 'chart-header-toggle-to-bar-button',
    toggleToLineButton: 'chart-header-toggle-to-line-button',
    currentElement: 'chart-header-current-element',
    prevElement: 'chart-header-prev-element',
  },
  activeProductiveChartNavigation: {
    leftArrow: 'active-productive-chart-navigation-left-arrow',
    rightArrow: 'active-productive-chart-navigation-right-arrow',
  },
  utilizationChartNavigation: {
    leftArrow: 'utilization-chart-navigation-left-arrow',
    rightArrow: 'utilization-chart-navigation-right-arrow',
  },
  performanceNcFiles: {
    container: 'performance-nc-files-container',
    item: 'performance-nc-files-item',
  },
  utilizationPopup: {
    container: 'utilization-popup-container',
    toGoButton: 'utilization-popup-togo-button',
    oeeColumn: 'utilization-popup-oee-column',
    teepColumn: 'utilization-popup-teep-column',
    effectiveness: 'utilization-popup-effectiveness',
    date: 'utilization-popup-date',
    verticalLine: 'utilization-popup-vertical-line',
  },
  utilizationHeader: {
    container: 'utilization-header-container',
    oeeColumn: 'utilization-header-oee-column',
    teepColumn: 'utilization-header-teep-column',
    effectiveness: 'utilization-header-effectiveness',
    dateText: 'utilization-header-date-text',
  },
  utilizationChart: {
    container: 'utilization-chart-container',
    chartArea: 'utilization-chart-area',
    rightLegend: 'utilization-chart-right-legend',
  },
  utilizationBar: {
    chartContainer: 'utilization-bar-chart-container',
    barsContainer: 'utilization-bar-bars-container',
    legendContainer: 'utilization-bar-legend-container',
    oeePercentageLabel: 'utilization-bar-oee-percentage',
  },
  dashboardOverview: {
    screen: 'dashboardOverview.screen',
    topBlock: 'dashboardOverview.topBlock',
    scopeTab: (scope: DashboardScope) => `dashboardOverview.scopeTab.${scope}`,
    resultTypeTrigger: 'dashboardOverview.resultTypeTrigger',
    resultTypeOption: (value: DashboardResultType) => `dashboardOverview.resultTypeOption.${value}`,
  },
  dashboardOverviewBottomBlock: {
    container: 'dashboard-overview-bottom-block',
    worstPanel: 'dashboard-overview-bottom-block-worst-panel',
    bestPanel: 'dashboard-overview-bottom-block-best-panel',
    actualPanel: 'dashboard-overview-bottom-block-actual-panel',
    showAllWorst: 'dashboard-overview-bottom-block-show-all-worst',
    showAllBest: 'dashboard-overview-bottom-block-show-all-best',
    showAllActual: 'dashboard-overview-bottom-block-show-all-actual',
    row: (variant: RankBadgeVariant, rank: number) =>
      `dashboard-overview-bottom-block-row-${variant}-${rank}`,
    noData: (variant: RankBadgeVariant) =>
      `dashboard-overview-bottom-block-${variant}-no-data`,
  },
  dashboardDrilldown: {
    screen: 'dashboardDrilldown.screen',
    breadcrumb: 'dashboardDrilldown.breadcrumb',
    ncFileBatchEvents: 'dashboardDrilldown.ncFileBatchEvents',
    tab: (tab: DashboardDrilldownTab) => `dashboardDrilldown.tab.${tab}`,
  },
  dashboardShowAll: {
    screen: 'dashboardShowAll.screen',
    header: {
      back: 'dashboardShowAll.header.back',
      resultTypeChip: 'dashboardShowAll.header.resultTypeChip',
      scopeTab: (scope: DashboardScope) => `dashboardShowAll.header.scopeTab.${scope}`,
    },
    list: {
      container: 'dashboardShowAll.list.container',
      row: (id: string) => `dashboardShowAll.list.row.${id}`,
      empty: 'dashboardShowAll.list.empty',
      error: 'dashboardShowAll.list.error',
      retry: 'dashboardShowAll.list.retry',
      legend: 'dashboardShowAll.list.legend',
    },
    summary: {
      loss: 'dashboardShowAll.summary.loss',
      gain: 'dashboardShowAll.summary.gain',
      net: 'dashboardShowAll.summary.net',
    },
  },
  paidBarChartHeader: {
    container: 'paid-bar-chart-header-container',
    lineColumn: 'paid-bar-chart-header-line-column',
    barColumn: 'paid-bar-chart-header-bar-column',
    quantity: 'paid-bar-chart-header-quantity',
    batches: 'paid-bar-chart-header-batches',
    verticalLine: 'paid-bar-chart-header-vertical-line',
  },
  paidBarChartBar: {
    chartContainer: 'paid-bar-chart-bar-container',
  },
  paidBarChart: {
    container: 'paid-bar-chart-container',
    chartArea: 'paid-bar-chart-area',
    rightLegend: 'paid-bar-chart-right-legend',
  },
  machinesHeader: {
    mainMenuPerformance: 'machines-header-main-menu-performance',
    mainMenuTimeline: 'machines-header-main-menu-timeline',
    mainMenuHistory: 'machines-header-main-menu-history',
    mainMenuScheduled: 'machines-header-main-menu-scheduled',
    performanceMenuUtilization: 'machines-header-performance-menu-utilization',
    performanceMenuActive: 'machines-header-performance-menu-active',
    performanceMenuProductive: 'machines-header-performance-menu-productive',
    performanceMenuPaid: 'machines-header-performance-menu-paid',
    timePeriodDay: 'machines-header-time-period-day',
    timePeriodWeek: 'machines-header-time-period-week',
    timePeriodMonth: 'machines-header-time-period-month',
    timePeriodYear: 'machines-header-time-period-year',
    timePeriodEightWeeks: 'machines-header-time-period-eight-weeks',
  },
  contextHeader: {
    timePeriodDay: 'context-header-time-period-day',
    timePeriodWeek: 'context-header-time-period-week',
    timePeriodMonth: 'context-header-time-period-month',
    timePeriodYear: 'context-header-time-period-year',
  },
  utilization: {
    chartBlock: 'utilization-chart-block',
    contentBlock: 'utilization-content-block',
    formulaCircles: 'utilization-formula-circles',
  },
  popups: {
    linkNcFileWithERP: 'link-nc-file-popup-with-ERP',
    linkNcFileWithoutERP: 'link-nc-file-popup-without-ERP',
    linkBatchWithERP: 'link-batch-popup-with-ERP',
    linkBatchWithoutERP: 'link-batch-popup-without-ERP',
  },
  ncFileBlock: {
    ncFileTypeRow: 'nc-file-block-nc-file-type-row',
    piecesPerCycleRow: 'nc-file-block-pieces-per-cycle-row',
    versionRow: 'nc-file-block-version-row',
  },
  ncFileLinks: {
    list: 'nc-file-links-list',
    empty: 'nc-file-links-empty',
    item: (index: number) => `nc-file-links-item-${index}`,
    itemPartName: (index: number) => `nc-file-links-item-${index}-part-name`,
    itemOperationName: (index: number) => `nc-file-links-item-${index}-operation-name`,
  },
  ncFileVersions: {
    loading: 'nc-file-versions-loading',
    compareButton: 'nc-file-versions-compare-button',
    addVersionButton: 'nc-file-versions-add-version-button',
    versionHeader: 'nc-file-versions-version-header',
    sendToMachineButton: 'nc-file-versions-send-to-machine-button',
    diffView: 'nc-file-versions-diff-view',
    comparisonSheet: 'nc-file-versions-comparison-sheet',
    versionPicker: 'nc-file-versions-version-picker',
    compareStartButton: 'nc-file-versions-compare-start-button',
    compareModeCancelButton: 'nc-file-versions-compare-mode-cancel-button',
  },
  sendToMachinePopup: {
    root: 'send-to-machine-popup-root',
    machineItem: (machineId: number) => `send-to-machine-popup-machine-${machineId}`,
    sendModal: 'send-to-machine-popup-send-modal',
  },
  ncFileDocuments: {
    empty: 'nc-file-documents-empty',
    galleryContainer: 'nc-file-documents-gallery-container',
    addButton: 'nc-file-documents-add-button',
  },
  documentsHeader: {
    title: 'documents-header-title',
    attachButton: 'documents-header-attach-button',
    docTypeButton: 'documents-header-doc-type-button',
    iconCrop: 'documents-header-icon-crop',
    iconRotate: 'documents-header-icon-rotate',
    iconShare: 'documents-header-icon-share',
    iconMore: 'documents-header-icon-more',
    iconExpand: 'documents-header-icon-expand',
  },
  documentsInfoPopup: {
    root: 'documents-info-popup-root',
    option: (key: string) => `documents-info-popup-option-${key}`,
  },
  documentsInfoModal: {
    root: 'documents-info-modal-root',
  },
  documentsAttachModal: {
    root: 'documents-attach-modal-root',
    attachToOther: 'documents-attach-modal-attach-to-other',
    itemFavoriteButton: (scopeId: number, scopeReferenceId: number) => `documents-attach-modal-item-${scopeId}-${scopeReferenceId}-favorite`,
    itemRemoveButton: (scopeId: number, scopeReferenceId: number) => `documents-attach-modal-item-${scopeId}-${scopeReferenceId}-remove`,
  },
  documentsRemoveAttachmentModal: {
    root: 'documents-remove-attachment-modal-root',
    confirmButton: 'documents-remove-attachment-modal-confirm',
  },
  documentsDocTypeModal: {
    root: 'documents-doc-type-modal-root',
  },
  documentsCropperOverlay: {
    root: 'documents-cropper-overlay-root',
  },
  imageHandlingPopup: {
    root: 'image-handling-popup-root',
  },
  ncFileBatchEvents: {
    row: (index: number) => `nc-file-batch-events-row-${index}`,
    subRow: (sequenceNumber: number) => `nc-file-batch-events-sub-row-${sequenceNumber}`,
    traceEventPopup: 'nc-file-batch-events-trace-event-popup',
  },
  batchTimeline: {
    container: 'batch-timeline-container',
  },
  runningBatch: {
    loading: 'running-batch-loading',
    notFound: 'running-batch-not-found',
    block: 'running-batch-block',
    productivityStatusBlock: 'running-batch-productivity-status-block',
    reloadButton: 'running-batch-reload-button',
    toggleButton: 'running-batch-toggle-button',
    setupHistogram: 'running-batch-setup-histogram',
    activeChangeHistograms: 'running-batch-active-change-histograms',
  },
  partGeneralContent: {
    loading: 'part-general-loading',
    block: 'part-general-block',
    sideImagesContainer: 'part-general-side-images-container',
    field: (key: string) => `part-general-field-${key}`,
    linkedNcFilesLine: 'part-general-linked-nc-files-line',
    linkedNcFilesCount: 'part-general-linked-nc-files-count',
  },
  jobContent: {
    loading: 'job-content-loading',
    topBlock: 'job-content-top-block',
    sideImagesContainer: 'job-content-side-images-container',
    field: (key: string) => `job-content-field-${key}`,
    blockTitle: (title: string) => `job-content-block-title-${title}`,
    machinesModal: 'job-content-machines-modal',
    productionOrderLink: 'job-content-production-order-link',
  },
  productionOrderContent: {
    loading: 'production-order-content-loading',
    topBlock: 'production-order-content-top-block',
    field: (key: string) => `production-order-content-field-${key}`,
    blockTitle: (title: string) => `production-order-content-block-title-${title}`,
    allErpFieldsButton: 'production-order-content-all-erp-fields-button',
  },
  productionOrderJobsList: {
    orderItem: 'production-order-jobs-list-order-item',
    orderItemSelected: 'production-order-jobs-list-order-item-selected',
    allJobsItem: 'production-order-jobs-list-all-jobs-item',
    allJobsItemSelected: 'production-order-jobs-list-all-jobs-item-selected',
    jobItem: (productionOperationId: number) => `production-order-jobs-list-job-item-${productionOperationId}`,
    jobItemSelected: (productionOperationId: number) => `production-order-jobs-list-job-item-${productionOperationId}-selected`,
  },
  clampingTool: {
    loading: 'clamping-tool-loading',
    emptyAddButton: 'clamping-tool-empty-add-button',
    topAddButton: 'clamping-tool-top-add-button',
    toolItem: (clampingToolId: number) => `clamping-tool-item-${clampingToolId}`,
    detailLine: (clampingToolId: number, fieldName: string) => `clamping-tool-detail-line-${clampingToolId}-${fieldName}`,
    detailsPopup: 'clamping-tool-details-popup',
    editFieldModal: 'clamping-tool-edit-field-modal',
  },
  ncFileVersionSelector: {
    card: (versionId: number) => `nc-file-version-card-${versionId}`,
    cardSelected: (versionId: number) => `nc-file-version-card-${versionId}-selected`,
    favoriteButton: (versionId: number) => `nc-file-version-card-${versionId}-favorite-button`,
  },
  piecesPerCycleModal: {
    root: 'pieces-per-cycle-modal',
    input: 'pieces-per-cycle-modal-input',
    submitButton: 'pieces-per-cycle-modal-submit-button',
  },
  webHeader: {
    settingsButton: 'web-header-settings-button',
  },
  tenantStatus: {
    layout: {
      container: 'tenant-status-layout',
      logOutButton: 'tenant-status-log-out',
    },
    onHold: {
      container: 'tenant-status-onhold',
      logOutButton: 'tenant-status-onhold-log-out',
      settingsButton: 'tenant-status-onhold-settings',
    },
    deactivated: {
      container: 'tenant-status-deactivated',
      contactUsButton: 'tenant-status-deactivated-contact-us',
      logOutButton: 'tenant-status-deactivated-log-out',
    },
    settingsLimited: {
      container: 'tenant-status-settings-limited',
    },
    onboardingWizard: {
      container: 'tenant-status-onboarding-wizard',
      card: 'tenant-status-onboarding-wizard-card',
      progress: 'tenant-status-onboarding-wizard-progress',
      welcomeGetStarted: 'tenant-status-onboarding-wizard-get-started',
      nextButton: 'tenant-status-onboarding-wizard-next',
      backButton: 'tenant-status-onboarding-wizard-back',
      submitButton: 'tenant-status-onboarding-wizard-submit',
      logOutButton: 'tenant-status-onboarding-wizard-log-out',
      thankYou: 'tenant-status-onboarding-wizard-thank-you',
    },
  },
  settingsNavigation: {
    accountItem: 'settings-navigation-account-item',
    usersItem: 'settings-navigation-users-item',
    devicesItem: 'settings-navigation-devices-item',
    machinesItem: 'settings-navigation-machines-item',
    notificationsItem: 'settings-navigation-notifications-item',
    connectorItem: 'settings-navigation-connector-item',
  },
  settingsScreen: {
    notificationsToggle: 'settings-notifications-toggle',
    emailReportsLink: 'settings-notifications-email-reports-link',
    weeklyDigestToggle: 'settings-email-reports-weekly-digest-toggle',
    accountProfileCard: 'settings-account-profile-card',
    accountInviteColleague: 'settings-account-invite-colleague',
    accountLanguageLine: 'settings-account-language-line',
    accountThemeLine: 'settings-account-theme-line',
    companyNotifyToggle: 'settings-company-notify-toggle',
    companyTimezoneRow: 'settings-company-timezone-row',
    companyLanguageRow: 'settings-company-language-row',
    companyProfileSection: 'settings-company-profile-section',
    companyDeleteButton: 'settings-company-delete-button',
    devicesTable: 'settings-devices-table',
    devicesWallboardSection: 'settings-devices-wallboard-section',
    devicesOtherSection: 'settings-devices-other-section',
    deviceDetailsContent: 'settings-device-details-content',
    deviceDetailsBlockButton: 'settings-device-details-block-button',
    deviceDetailsDeleteButton: 'settings-device-details-delete-button',
  },
  apiKeysScreen: {
    emptyState: 'settings-api-keys-empty-state',
    generateButton: 'settings-api-keys-generate-button',
    keyCard1: 'settings-api-keys-key-card-1',
    keyCard2: 'settings-api-keys-key-card-2',
    deleteButton: 'settings-api-keys-delete-button',
  },
  machineDetails: {
    countSettingsLine: 'machine-details-count-settings-line',
    operationsLinksLine: 'machine-details-operations-links-line',
  },
  machineCountSettings: {
    content: 'machine-count-settings-content',
    machineName: 'machine-count-settings-machine-name',
    connectionMethodName: 'machine-count-settings-connection-method-name',
    selectAutomaticallyToggle: 'machine-count-settings-select-automatically-toggle',
    selectAutomaticallyBlock: 'machine-count-settings-select-automatically-block',
    countingMethodsBlock: 'machine-count-settings-counting-methods-block',
    countingMethodRow: (method: string) => `machine-count-settings-counting-method-row-${method}`,
    quantityBlock: 'machine-count-settings-quantity-block',
    automaticInfoBox: 'machine-count-settings-automatic-info-box',
    automaticInfoText: 'machine-count-settings-automatic-info-text',
    countingMethodsLoading: 'machine-count-settings-counting-methods-loading',
  },
  machineAssignedOperations: {
    operationBlock: (operationId: number) => `machine-assigned-operations-block-${operationId}`,
  },
  timeline: {
    container: 'timeline-container',
    eventsPanel: 'timeline-events-panel',
    multiTimelineContainer: 'multi-timeline-container',
  },
  dateHeader: {
    zoomIn: 'date-header-zoom-in',
    zoomOut: 'date-header-zoom-out',
    zoomReset: 'date-header-zoom-reset',
    leftArrow: 'date-header-left-arrow',
    rightArrow: 'date-header-right-arrow',
    todayButton: 'date-header-today-button',
    dateLabel: 'date-header-date-label',
  },
  productivityStatus: {
    showEventsButton: 'productivity-status-show-events-button',
    netUtilizationButton: 'productivity-status-net-utilization-button',
  },
  searchScreen: {
    statisticContainer: 'search-statistic-container',
    suggestionContainer: 'search-suggestion-container',
    suggestionItem: 'search-suggestion-item',
    cancelSearchButton: 'search-cancel-search-button',
    clearSearchButton: 'search-clear-search-button',
    modeSelectorItem: 'search-mode-selector-item',
    scrollView: 'search-scroll-view',
    levelSelector: 'search-level-selector',
    statisticTiles: {
      unknownNcFile: 'search-statistic-tile-unknown-nc-file',
      notLinked: 'search-statistic-tile-not-linked',
      singleLink: 'search-statistic-tile-single-link',
      multiLink: 'search-statistic-tile-multi-link',
      linkedNcFile: 'search-statistic-tile-linked-nc-file',
      late: 'search-statistic-tile-late',
      nowWeekend: 'search-statistic-tile-now-weekend',
      nextWeek: 'search-statistic-tile-next-week',
      afterNextWeek: 'search-statistic-tile-after-next-week',
      finishedThisWeek: 'search-statistic-tile-finished-this-week',
      thisWeek: 'search-statistic-tile-this-week',
      unfinished: 'search-statistic-tile-unfinished',
      finishedToday: 'search-statistic-tile-finished-today',
      modifiedThisWeek: 'search-statistic-tile-modified-this-week',
      createdThisWeek: 'search-statistic-tile-created-this-week',
      partImages: 'search-statistic-tile-part-images',
      twoDDrawings: 'search-statistic-tile-2d-drawings',
      notAttached: 'search-statistic-tile-not-attached',
    },
    operations: {
      dataGridContainer: 'search-operations-data-grid-container',
      infoTileContainer: 'search-operations-info-tile-container',
      itemContainer: 'search-operations-item-container',
      infoTile: 'search-operations-info-tile',
      infoTileImage: 'search-operations-info-tile-image',
      itemTile: 'search-operations-item-tile',
      itemImage: 'search-operations-item-image',
      dataGridImage: 'search-operations-data-grid-image',
    },
    parts: {
      dataGridContainer: 'search-parts-data-grid-container',
      itemContainer: 'search-parts-item-container',
      dataGridImage: 'search-parts-data-grid-image',
      itemTile: 'search-parts-item-tile',
    },
    ncFiles: {
      dataGridContainer: 'search-nc-files-data-grid-container',
      itemContainer: 'search-nc-files-item-container',
      dataGridImage: 'search-nc-files-data-grid-image',
      itemTile: 'search-nc-files-item-tile',
      updateNcFileTypeButton: 'search-nc-files-update-nc-file-type-button',
    },
    tools: {
      dataGridContainer: 'search-tools-data-grid-container',
      itemContainer: 'search-tools-item-container',
    },
    imagesDocuments: {
      dataGridContainer: 'search-images-documents-data-grid-container',
      itemContainer: 'search-images-documents-item-container',
    },
    productionOrders: {
      dataGridContainer: 'search-production-orders-data-grid-container',
      infoTileContainer: 'search-production-orders-info-tile-container',
      itemContainer: 'search-production-orders-item-container',
      dataGridImage: 'search-production-orders-data-grid-image',
      itemTile: 'search-production-orders-item-tile',
      itemImage: 'search-production-orders-item-image',
    },
    productionBatches: {
      dataGridContainer: 'search-production-batches-data-grid-container',
      batchListContainer: 'search-production-batches-batch-list-container',
      itemContainer: 'search-production-batches-item-container',
      batchHoverButton: 'search-production-batches-batch-hover-button',
      infoTileTile: 'search-production-batches-info-tile-tile',
      infoTileImage: 'search-production-batches-info-tile-image',
      squareTileTile: 'search-production-batches-square-tile-tile',
      squareTileImage: 'search-production-batches-square-tile-image',
      lineTile: 'search-production-batches-line-tile',
      lineImage: 'search-production-batches-line-image',
      dataGridRow: 'search-production-batches-data-grid-row',
      dataGridImage: 'search-production-batches-data-grid-image',
    },
    all: {
      searchResultsContainer: 'search-all-search-results-container',
      searchResultItem: 'search-all-search-result-item',
      historyContainer: 'search-all-history-container',
      historyItem: 'search-all-history-item',
    },
  },
  scheduledScreen: {
    filterSelectorMachine: 'scheduled-filter-selector-machine',
    filterSelectorMatchedOps: 'scheduled-filter-selector-matched-ops',
    filterSelectorAllOps: 'scheduled-filter-selector-all-ops',
  },
  historyScreen: {
    batchListScrollView: 'history-batch-list-scroll-view',
  },
  usersScreen: {
    invitedSection: 'settings-users-invited-section',
    activeSection: 'settings-users-active-section',
    invitedTable: 'settings-users-invited-table',
    activeTable: 'settings-users-active-table',
  },
  userDetailsScreen: {
    content: 'settings-user-details-content',
    blockButton: 'settings-user-details-block-button',
    deleteButton: 'settings-user-details-delete-button',
    devicesSection: 'settings-user-details-devices-section',
  },
  supportScreen: {
    askForHelpSection: 'settings-support-ask-for-help',
    feedbackSection: 'settings-support-feedback',
    versionSection: 'settings-support-version',
    environmentSection: 'settings-support-environment',
  },
  imnooScreen: {
    apiKeyInput: 'settings-imnoo-api-key-input',
    saveButton: 'settings-imnoo-save-button',
    infoSection: 'settings-imnoo-info-section',
  },
  connectorScreen: {
    hostDetailsSection: 'settings-connector-host-details',
    machinesCard: 'settings-connector-machines-card',
    filesCard: 'settings-connector-files-card',
    productionCard: 'settings-connector-production-card',
    resetButton: 'settings-connector-reset-button',
    noConnectorView: 'settings-connector-no-connector',
  },
  detailsPage: {
    closeButton: 'details-page-close-button',
    moreButton: 'details-page-more-button',
    backButton: 'details-page-back-button',
    backButtonText: 'details-page-back-button-text',
    levelTab: (entity: string) => `details-page-level-tab-${entity}`,
    levelTabPerspectiveBadge: (entity: string) => `details-page-level-tab-perspective-badge-${entity}`,
    levelSubScreen: (subScreen: string) => `details-page-level-sub-screen-${subScreen}`,
    doneButton: 'details-page-done-button',
  },
  traceabilityGraph: {
    container: 'traceability-graph-container',
    surface: 'traceability-graph-surface',
    column: (columnId: string) => `traceability-graph-column-${columnId}`,
    node: (nodeId: string) => `traceability-graph-node-${nodeId}`,
    emptyState: 'traceability-graph-empty-state',
    loading: 'traceability-graph-loading',
  },
  imagesContainer: {
    emptyFlatLine: 'images-container-empty-flat-line',
    addButton: 'images-container-add-button',
    image: (contentHash: string) => `images-container-image-${contentHash}`,
  },
  imageUploadPopup: {
    container: 'image-upload-popup-container',
    tag: (scopeId: number | string, scopeReferenceId: number | string) => `image-upload-popup-tag-${scopeId}-${scopeReferenceId}`,
    preselectedTag: (scopeId: number | string, scopeReferenceId: number | string) =>
      `image-upload-popup-tag-${scopeId}-${scopeReferenceId}-preselected`,
  },
  detailsModal: {
    ncFile: {
      edit: 'details-modal-nc-file-edit',
      download: 'details-modal-nc-file-download',
      sendToMachine: 'details-modal-nc-file-send-to-machine',
      uploadNewVersion: 'details-modal-nc-file-upload-new-version',
      markInactive: 'details-modal-nc-file-mark-inactive',
      clearNcFileType: 'details-modal-nc-file-clear-nc-file-type',
    },
    part: {
      edit: 'details-modal-part-edit',
      delete: 'details-modal-part-delete',
    },
    tools: {
      add: 'details-modal-tools-add',
    },
  },
  alert: {
    container: 'alert-container',
    title: 'alert-title',
    confirmRemove: 'alert-action-confirm-remove',
    cancel: 'alert-action-cancel',
  },
  clampingToolDetails: {
    newToolPopup: 'clamping-tool-details-new-tool-popup',
  },
  operatorPanel: {
    entry: {
      openButton: 'operator-panel-entry-open-button',
    },
    home: {
      informationButton: 'operator-panel-home-information-button',
      machineName: 'operator-panel-home-machine-name',
      ncFileName: 'operator-panel-home-nc-file-name',
      operatorBlockButton: 'operator-panel-home-operator-block',
      scheduledButton: 'operator-panel-home-scheduled-button',
      selectNCFileTypeButton: 'operator-panel-home-select-nc-file-type-button',
    },
    ncFileTypeModal: {
      root: 'operator-panel-nc-file-type-modal',
    },
    information: {
      closeButton: 'operator-panel-information-close-button',
      tab: {
        jobs: 'operator-panel-information-tab-jobs',
        ncFiles: 'operator-panel-information-tab-nc-files',
        setup: 'operator-panel-information-tab-setup',
        tools: 'operator-panel-information-tab-tools',
        notes: 'operator-panel-information-tab-notes',
        searchNCFiles: 'operator-panel-information-tab-search-nc-files',
        part: 'operator-panel-information-tab-part',
        twoDDrawing: 'operator-panel-information-tab-2d-drawing',
        threeDDrawing: 'operator-panel-information-tab-3d-drawing',
        clamping: 'operator-panel-information-tab-clamping',
        productionOrder: 'operator-panel-information-tab-production-order',
        measurement: 'operator-panel-information-tab-measurement',
        allDocuments: 'operator-panel-information-tab-all-documents',
        searchHistory: 'operator-panel-information-tab-search-history',
      },
      notes: {
        emptyScreen: 'operator-panel-information-notes-empty-screen',
        editScreen: 'operator-panel-information-notes-edit-screen',
      },
      searchNCFiles: {
        root: 'operator-panel-information-search-nc-files',
      },
      jobs: {
        root: 'operator-panel-information-jobs-screen',
        emptyScreen: 'operator-panel-information-jobs-empty-screen',
      },
      ncFiles: {
        root: 'operator-panel-information-nc-files-screen',
        versionRow: 'operator-panel-information-nc-files-version-row',
      },
      setup: {
        root: 'operator-panel-information-setup-screen',
      },
      tools: {
        root: 'operator-panel-information-tools-screen',
      },
      part: {
        root: 'operator-panel-information-part-screen',
      },
      twoDDrawing: {
        root: 'operator-panel-information-2d-drawing-screen',
      },
      threeDDrawing: {
        root: 'operator-panel-information-3d-drawing-screen',
      },
      clamping: {
        root: 'operator-panel-information-clamping-screen',
      },
      productionOrder: {
        root: 'operator-panel-information-production-order-screen',
      },
      measurement: {
        root: 'operator-panel-information-measurement-screen',
      },
      allDocuments: {
        root: 'operator-panel-information-all-documents-screen',
      },
      searchHistory: {
        root: 'operator-panel-information-search-history-screen',
      },
    },
    settings: {
      closeButton: 'operator-panel-settings-close-button',
      goToMainAppButton: 'operator-panel-settings-go-to-main-app-button',
      moreButton: 'operator-panel-settings-more-button',
      actionsMenuGoToMainApp: 'operator-panel-settings-actions-menu-go-to-main-app',
      actionsMenuLogOff: 'operator-panel-settings-actions-menu-log-off',
    },
    unavailable: {
      machineOffline: 'operator-panel-unavailable-machine-offline',
      connectorOffline: 'operator-panel-unavailable-connector-offline',
      notProductive: 'operator-panel-unavailable-not-productive',
    },
  },
  drawingCanvas: {
    container: 'drawing-canvas-container',
    layer: 'drawing-canvas-layer',
    topBar: 'drawing-canvas-top-bar',
    doneButton: 'drawing-canvas-done-button',
    cancelButton: 'drawing-canvas-cancel-button',
    deleteButton: 'drawing-canvas-delete-button',
    bottomToolbar: 'drawing-canvas-bottom-toolbar',
    toolPointer: 'drawing-canvas-tool-pointer',
    toolPen: 'drawing-canvas-tool-pen',
    toolHighlighter: 'drawing-canvas-tool-highlighter',
    toolEraser: 'drawing-canvas-tool-eraser',
    toolArrow: 'drawing-canvas-tool-arrow',
    toolRectangle: 'drawing-canvas-tool-rectangle',
    toolCircle: 'drawing-canvas-tool-circle',
    toolText: 'drawing-canvas-tool-text',
    undoButton: 'drawing-canvas-undo-button',
    redoButton: 'drawing-canvas-redo-button',
    colorSwatch: 'drawing-canvas-color-swatch',
    widthSwatch: 'drawing-canvas-width-swatch',
    fillOutline: 'drawing-canvas-fill-outline',
    fillFilled: 'drawing-canvas-fill-filled',
    annotationList: 'drawing-canvas-annotation-list',
    annotationListItem: 'drawing-canvas-annotation-list-item',
    textInputOverlay: 'drawing-canvas-text-input-overlay',
  },
} as const;

export const possibleReasonsOfSubStatusOffline = [
  'Machine Offline',
  'No Connection Possible',
  'Machine Off',
  'Connection Switched Off',
  'Connector Offline',
  'No Connector',
];

export const HOUR_IN_SECONDS = 60 * 60;

export const CHECK_API_CACHE = {
  KEY: 'checkApiCache',
  TTL_MS: 24 * 60 * 60 * 1000,
} as const;

export const BULLET = '\u2022';
export const LOGS_AMOUNT_TO_SEND_IN_FEEDBACK = 20;
